import { useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { Button } from '../../components/ui';
import frontGuide from '../../assets/onboarding/front.png';

export function FrontPhotoScreen() {
  const navigate = useNavigate();
  const { rating, updateRating, nextStep, user } = useStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be under 10MB.');
      return;
    }

    const preview = URL.createObjectURL(file);
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = typeof reader.result === 'string' ? reader.result : '';
      if (base64) {
        localStorage.setItem('umax-front-base64', base64);
      }
    };
    reader.readAsDataURL(file);
    if (user.userId) {
      localStorage.removeItem(`umax-analysis-submitted-${user.userId}`);
    }

    updateRating({
      frontPhoto: file,
      frontPhotoPreview: preview,
    });
  }, [updateRating, user.userId]);

  const handleContinue = () => {
    if (rating.frontPhoto) {
      nextStep();
      navigate('/rating/side');
    }
  };

  const handleRetake = () => {
    updateRating({
      frontPhoto: null,
      frontPhotoPreview: null,
    });
    localStorage.removeItem('umax-front-base64');
    if (user.userId) {
      localStorage.removeItem(`umax-analysis-submitted-${user.userId}`);
    }
  };

  return (
    <div className="screen-container">
      <h1 className="text-3xl font-bold mb-2">Front photo</h1>
      <p className="text-secondary mb-8">
        Face the camera directly. Good lighting helps.
      </p>

      {/* Photo preview area */}
      <div className="flex-1 flex flex-col items-center justify-center mb-4">
        {rating.frontPhotoPreview ? (
          <div className="relative">
            <img
              src={rating.frontPhotoPreview}
              alt="Front preview"
              className="w-80 h-80 object-cover rounded-2xl"
            />
            <button
              onClick={handleRetake}
              className="absolute top-2 right-2 p-2 bg-black/50 rounded-full"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="w-80 h-80 rounded-2xl bg-surface border-2 border-default overflow-hidden">
            <img
              src={frontGuide}
              alt="Front pose guide"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="mt-2" />

      </div>

      {/* Action buttons */}
      {!rating.frontPhotoPreview && (
        <div className="space-y-3 mb-6">
          <Button onClick={() => fileInputRef.current?.click()}>
            <span className="flex items-center justify-center gap-2 w-full">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Upload
            </span>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      )}

      <Button onClick={handleContinue} disabled={!rating.frontPhoto} className="mt-auto">
        Continue
      </Button>
    </div>
  );
}
